// src/components/ui/illustrations/PlateFill.tsx
// One circular plate, filled to a fraction — the semantic element of a diet
// choice-matrix (Phase 2): the nurse compares fills, not words. Part of
// docs/HMS_V4_PRECONSULT_REDESIGN_PLAN.md Phase 0.
//
// Token-driven only (var(--color-…)), no hex literals — this lives under
// components/ui/**, where both design-system ESLint rules apply. The FILL
// FRACTION itself carries the verdict (paired with a StatusFace at the call
// site for a second channel), so the matrix still reads correctly in
// greyscale — see docs/design/preconsult/Details.dc.html's plate-fill board.

export type IllustrationTone = 'success' | 'warn' | 'danger';
export type IllustrationSize = 'sm' | 'md' | 'lg';

/** Fixed 24/32/40 scale per the redesign plan's Phase 0 — never an arbitrary size. */
export const ILLUSTRATION_SIZE_PX: Record<IllustrationSize, number> = { sm: 24, md: 32, lg: 40 };

export const TONE_FILL_VAR: Record<IllustrationTone, string> = {
  success: 'var(--color-success)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-danger)',
};

export const TONE_INK_VAR: Record<IllustrationTone, string> = {
  success: 'var(--color-success-ink)',
  warn: 'var(--color-warn-ink)',
  danger: 'var(--color-danger-ink)',
};

export type PlateFraction = '1/3' | '1/2' | '2/3' | 'full';

export interface PlateFillProps {
  fraction: PlateFraction;
  tone: IllustrationTone;
  size?: IllustrationSize;
  /** Native SVG tooltip only — the accessible name comes from the matrix
   * cell's own label (this drawing is `aria-hidden`), per the field-types
   * spec's rendering notes. */
  title?: string;
}

export function PlateFill({ fraction, tone, size = 'md', title }: PlateFillProps) {
  const px = ILLUSTRATION_SIZE_PX[size];
  const color = TONE_FILL_VAR[tone];
  return (
    <svg width={px} height={px} viewBox="0 0 34 34" aria-hidden="true" focusable="false">
      {title && <title>{title}</title>}
      <circle cx="17" cy="17" r="15" fill="var(--color-muted)" stroke="var(--color-control-line)" />
      {fraction === '1/3' && <path d="M17 6a11 11 0 0 1 9.5 5.5L17 17z" fill={color} opacity={0.8} />}
      {fraction === '1/2' && <path d="M17 6a11 11 0 0 1 0 22z" fill={color} opacity={0.8} />}
      {fraction === '2/3' && <path d="M17 6a11 11 0 1 1 -9.5 16.5L17 17z" fill={color} opacity={0.8} />}
      {fraction === 'full' && <circle cx="17" cy="17" r="11" fill={color} opacity={0.8} />}
    </svg>
  );
}
